import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _createForOfIteratorHelper(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

import React, { createContext, useCallback, useContext } from 'react';

/**
 * Exactly the same as {@see createEventChannel} except that the Listener "catches"
 * the event. That is to say, you have to manually redispatch the event on the
 * same event channel if you want a Listener further up in the component hierachy to
 * also catch the event.
 */
export var createCatchingEventChannel = function createCatchingEventChannel() {
  var defaultOnEventCallback = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : undefined;
  var context = /*#__PURE__*/createContext(defaultOnEventCallback);

  var useEventDispatch = function useEventDispatch() {
    var onEventCallback = useContext(context);
    var fireEventCallback = useCallback(function (event) {
      onEventCallback === null || onEventCallback === void 0 ? void 0 : onEventCallback(event);
    }, [onEventCallback]);
    return fireEventCallback;
  };

  var EventCatcher = function EventCatcher(_ref) {
    var onEvent = _ref.onEvent,
        children = _ref.children;
    return /*#__PURE__*/React.createElement(context.Provider, {
      value: onEvent
    }, children);
  };

  return {
    Listener: EventCatcher,
    useEventDispatch: useEventDispatch
  };
};
/**
 * Creates a {@link UseEventDispatchHook} you can dispatch events from and a {@link Listener} you can listen to those
 * events from.
 *
 * Note that dispatched events will be picked up ONLY by the dispatching component's closest parent.
 */

export var createEventChannel = function createEventChannel() {
  var catchingEventChannel = createCatchingEventChannel();
  var ListenerThatDoesntConsumeEvent = withRedispatch({
    from: catchingEventChannel.Listener,
    to: catchingEventChannel.useEventDispatch
  });
  return {
    Listener: ListenerThatDoesntConsumeEvent,
    useEventDispatch: catchingEventChannel.useEventDispatch
  };
};
export var combineListeners = function combineListeners() {
  for (var _len = arguments.length, listenersToCombine = new Array(_len), _key = 0; _key < _len; _key++) {
    listenersToCombine[_key] = arguments[_key];
  }

  var CombinedListener = function CombinedListener(_ref2) {
    var children = _ref2.children,
        onEvent = _ref2.onEvent;
    var wrappedChildren = children;

    var _iterator = _createForOfIteratorHelper(listenersToCombine),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var ListenerToCombine = _step.value;
        wrappedChildren = ListenerToCombine({
          onEvent: onEvent,
          children: wrappedChildren
        });
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    return /*#__PURE__*/React.createElement(React.Fragment, null, wrappedChildren);
  };

  return CombinedListener;
};

/**
 * Create a React component that listens to one type of event, transforms it and then
 * dispatches the transformed payload as another event.
 *
 * @see {@link createEventChannel}
 */
export var createTransformer = function createTransformer(_ref3) {
  var inboundListenerValue = _ref3.from,
      useOutboundEventDispatch = _ref3.to,
      useTransformer = _ref3.useTransformer;
  var InboundListener = Array.isArray(inboundListenerValue) ? combineListeners.apply(void 0, _toConsumableArray(inboundListenerValue)) : inboundListenerValue;

  var Transformer = function Transformer(_ref4) {
    var children = _ref4.children,
        transformerProps = _objectWithoutProperties(_ref4, ["children"]);

    var dispatchTransformedEvent = useOutboundEventDispatch();
    var transform = useTransformer(transformerProps);
    var transformFn = useCallback(function (payload) {
      var transformed = transform(payload);
      dispatchTransformedEvent(transformed);
    }, [dispatchTransformedEvent, transform]);
    return /*#__PURE__*/React.createElement(InboundListener, {
      onEvent: transformFn
    }, children);
  };

  return Transformer;
};
/**
 * @returns A listener that redispatches the event to a given callback.
 * A very common usecase for this is if you want to allow multiple listeners to listen to the same
 * event in the parent tree from listening to events.
 */

export var withRedispatch = function withRedispatch(_ref5) {
  var from = _ref5.from,
      to = _ref5.to;
  var ListenerThatDoesntConsumeEvent = createTransformer({
    from: from,
    to: to,
    useTransformer: function useTransformer(_ref6) {
      var onEvent = _ref6.onEvent;
      return useCallback(function (payload) {
        onEvent(payload);
        return payload;
      }, [onEvent]);
    }
  });
  return ListenerThatDoesntConsumeEvent;
};

/**
 * @param filter A callback that is called per caught event. If the callback returns false, onEvent won't be called.
 */
export var withFilter = function withFilter(Listener, filter) {
  var FilteredListener = function FilteredListener(_ref7) {
    var onEvent = _ref7.onEvent,
        other = _objectWithoutProperties(_ref7, ["onEvent"]);

    var filteredOnEvent = useCallback(function (eventPayload) {
      if (filter(eventPayload)) {
        onEvent(eventPayload);
      }
    }, [onEvent]);
    return Listener(_objectSpread({
      onEvent: filteredOnEvent
    }, other));
  };

  return FilteredListener;
};
// TODO: Variadic types will make this type definition work properly
export var nestListeners = function nestListeners(children, components) {
  var wrappedChildren = /*#__PURE__*/React.createElement(React.Fragment, null, children);

  for (var i = components.length - 1; i >= 0; i--) {
    var _components$i = components[i],
        _Listener = _components$i.Listener,
        onEvent = _components$i.onEvent;
    wrappedChildren = /*#__PURE__*/React.createElement(_Listener, {
      onEvent: onEvent
    }, wrappedChildren);
  }

  return wrappedChildren;
};